import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, TagIcon, FileTextIcon, BellIcon, SmileIcon, PaperclipIcon, SendIcon, PlusIcon } from 'lucide-react';
import { MessageBubble } from '../components/MessageBubble';
import { ClientInfoOverlay } from '../components/ClientInfoOverlay';
import { TemplatePickerModal } from '../components/TemplatePickerModal';
import { AddNoteModal } from '../components/AddNoteModal';
import { MessageOptionsMenu } from '../components/MessageOptionsMenu';
interface Message {
  id: string;
  text: string;
  timestamp: string;
  isOutgoing: boolean;
  isImportant?: boolean;
}
export function ChatWorkspace() {
  const navigate = useNavigate();
  const {
    clientId
  } = useParams();
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    text: 'Hi Dr. Sarah! I wanted to check in about my meal plan.',
    timestamp: '10:30 AM',
    isOutgoing: false
  }, {
    id: '2',
    text: 'Hello Priya! Of course, how has the first week been going?',
    timestamp: '10:32 AM',
    isOutgoing: true
  }, {
    id: '3',
    text: "It's been good! I've been following the breakfast and lunch plans. The only challenge is dinner timing because of work.",
    timestamp: '10:35 AM',
    isOutgoing: false
  }, {
    id: '4',
    text: "That's completely normal. Let's adjust your dinner plan to fit your schedule better. What time do you usually get home?",
    timestamp: '10:36 AM',
    isOutgoing: true
  }]);
  const [inputText, setInputText] = useState('');
  const [showClientInfo, setShowClientInfo] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [showMessageOptions, setShowMessageOptions] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const handleSend = () => {
    if (inputText.trim()) {
      const newMessage: Message = {
        id: Date.now().toString(),
        text: inputText,
        timestamp: new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        }),
        isOutgoing: true
      };
      setMessages([...messages, newMessage]);
      setInputText('');
    }
  };
  const handleMessageSwipe = (message: Message) => {
    setSelectedMessage(message);
    setShowAddNote(true);
  };
  const handleMessageLongPress = (message: Message) => {
    setSelectedMessage(message);
    setShowMessageOptions(true);
  };
  const handleMarkImportant = () => {
    if (selectedMessage) {
      setMessages(messages.map(msg => msg.id === selectedMessage.id ? {
        ...msg,
        isImportant: !msg.isImportant
      } : msg));
    }
    setShowMessageOptions(false);
  };
  const handleDeleteMessage = () => {
    if (selectedMessage) {
      setMessages(messages.filter(msg => msg.id !== selectedMessage.id));
    }
    setShowMessageOptions(false);
  };
  const isEmpty = messages.length === 0;
  return <div className="min-h-screen bg-[#ECE5DD] flex flex-col">
      <div className="bg-[#5B8E87] text-white shadow-md sticky top-0 z-20">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3 flex-1">
            <button onClick={() => navigate(-1)} className="text-white hover:opacity-80">
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => setShowClientInfo(true)}>
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold text-sm">PS</span>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-base truncate">Priya Shah</h2>
                <p className="text-xs text-white text-opacity-90">
                  Active Client
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowClientInfo(true)} className="p-2 hover:bg-white hover:bg-opacity-10 rounded-full transition-colors" title="Add Label">
              <TagIcon className="w-5 h-5" />
            </button>
            <button onClick={() => setShowClientInfo(true)} className="p-2 hover:bg-white hover:bg-opacity-10 rounded-full transition-colors" title="View Notes">
              <FileTextIcon className="w-5 h-5" />
            </button>
            <button onClick={() => setShowClientInfo(true)} className="p-2 hover:bg-white hover:bg-opacity-10 rounded-full transition-colors" title="Set Reminder">
              <BellIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-32">
        {isEmpty ? <div className="flex items-center justify-center h-full">
            <div className="bg-white rounded-2xl shadow-sm p-8 max-w-sm text-center">
              <div className="w-16 h-16 bg-[#5B8E87] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">👋</span>
              </div>
              <p className="text-gray-600">
                You haven't chatted with Priya yet. Say hi to start your
                conversation.
              </p>
            </div>
          </div> : messages.map(message => <MessageBubble key={message.id} message={message} onSwipe={() => handleMessageSwipe(message)} onLongPress={() => handleMessageLongPress(message)} onMenuClick={() => handleMessageLongPress(message)} />)}
        <div ref={messagesEndRef} />
      </div>
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <button onClick={() => setShowTemplatePicker(true)} className="w-full py-2 px-4 text-[#5B8E87] text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors border-b border-gray-100">
          <PlusIcon className="w-4 h-4" />
          Insert Template
        </button>
        <div className="flex items-center gap-2 p-3">
          <button className="p-2 text-gray-500 hover:text-gray-700 transition-colors">
            <SmileIcon className="w-6 h-6" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 transition-colors">
            <PaperclipIcon className="w-6 h-6" />
          </button>
          <input type="text" value={inputText} onChange={e => setInputText(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSend()} placeholder="Type a message…" className="flex-1 px-4 py-3 rounded-full border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all" />
          <button onClick={handleSend} disabled={!inputText.trim()} className={`p-3 rounded-full transition-colors ${inputText.trim() ? 'bg-[#5B8E87] text-white hover:bg-[#4F7A74]' : 'bg-gray-200 text-gray-400'}`}>
            <SendIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
      {showClientInfo && <ClientInfoOverlay onClose={() => setShowClientInfo(false)} />}
      {showTemplatePicker && <TemplatePickerModal onClose={() => setShowTemplatePicker(false)} onSelect={template => {
      setInputText(template);
      setShowTemplatePicker(false);
    }} />}
      {showAddNote && <AddNoteModal message={selectedMessage} clientId={clientId} onClose={() => {
      setShowAddNote(false);
      setSelectedMessage(null);
    }} />}
      {showMessageOptions && <MessageOptionsMenu message={selectedMessage} onMarkImportant={handleMarkImportant} onCopy={() => {
      if (selectedMessage) {
        navigator.clipboard.writeText(selectedMessage.text);
      }
      setShowMessageOptions(false);
    }} onSaveToNotes={() => {
      setShowMessageOptions(false);
      setShowAddNote(true);
    }} onDelete={handleDeleteMessage} onClose={() => {
      setShowMessageOptions(false);
      setSelectedMessage(null);
    }} />}
    </div>;
}